import type { ReactElement, ReactNode } from 'react';
import type { UserRole } from '@xdocs/schemas';
import { EmptyState, Skeleton, useI18n, cn } from '@xdocs/ui';
import { Forbidden } from './Forbidden.js';
import { useAdminT } from './useAdminT.js';
import { useCurrentRole } from './useCurrentRole.js';
import { roleSatisfies } from './roleRank.js';
import styles from './RoleGate.module.css';

export interface RoleGateProps {
  /** Minimum platform role required to render `children` (F68). */
  minimum: UserRole;
  children: ReactNode;
}

/**
 * Client-side role gate (E19 all-states: loading/error/forbidden/populated)
 * for every admin route. UX-only — the real authorization decision is
 * server-side, fail-closed, on each `admin.*`/`impersonation.*` WS op
 * (§15.2 "App REFLECTS role... enforcement is server-side at the action").
 * A denied gate here just avoids flashing staff-only chrome at a
 * non-staff account; it is never the security boundary.
 */
export function RoleGate({ minimum, children }: RoleGateProps): ReactElement {
  const { t } = useI18n();
  const { t: tAdmin } = useAdminT();
  const { role, isLoading, isError, refetch } = useCurrentRole();

  if (isLoading) {
    return (
      <div className={cn(styles['loading'])} aria-busy="true">
        <Skeleton shape="tile" height="120px" />
        <Skeleton shape="row" count={4} />
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        kind="error"
        title={tAdmin('admin:roleGate.error.title')}
        action={{ label: t('common:action.retry'), onClick: refetch }}
      />
    );
  }

  if (!roleSatisfies(role, minimum)) {
    return <Forbidden />;
  }

  return <>{children}</>;
}
